<template>
  <div id="permissions-body">
    <!-- Internal Section -->
    <div id="internal" class="collapsible-section">
      <h2 class="subheader">Internal</h2>
      <VasionButton
        id="internal-collapse-section"
        name="internal-collapse-section"
        class="collapse-section"
        :icon="showInternal ? 'VasionArrowDropDownIcon' : 'VasionArrowDropDownRight16Icon'"
        title="Collapse Section"
        @vasionButtonClicked="toggleInternal()"
      />
      <div v-show="showInternal">
        <!-- Internal Users -->
        <div class="setup-row">
          <div class="section-content">
            <VasionListSelection
              :available-fields.sync="availableInternalUsers"
              :selected-fields.sync="assignedInternalUsers"
              :allow-field-ordering="false"
              available-label-text="Users"
              selected-label-text="Permitted Users"
              unique-id="permissions-internal-users"
              @update:availableFields="emitPermissionsChanged"
              @update:selectedFields="emitPermissionsChanged"
              @update:addAll="emitPermissionsChanged"
              @update:removeAll="emitPermissionsChanged"
            />
          </div>
        </div>
        <div class="subsection-separator"></div>
        <!-- Internal Groups -->
        <div class="setup-row">
          <div class="section-content">
            <VasionListSelection
              :available-fields.sync="availableInternalGroups"
              :selected-fields.sync="assignedInternalGroups"
              :allow-field-ordering="false"
              available-label-text="Groups"
              selected-label-text="Permitted Groups"
              unique-id="permissions-internal-groups"
              @update:availableFields="emitPermissionsChanged"
              @update:selectedFields="emitPermissionsChanged"
              @update:addAll="emitPermissionsChanged"
              @update:removeAll="emitPermissionsChanged"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="separator-div" />
    <!-- Portal Section -->
    <div id="portal" class="collapsible-section">
      <h2 class="subheader">Portal</h2>
      <VasionButton
        id="portal-collapse-section"
        name="portal-collapse-section"
        class="collapse-section"
        :icon="showPortal ? 'VasionArrowDropDownIcon' : 'VasionArrowDropDownRight16Icon'"
        title="Collapse Section"
        @vasionButtonClicked="togglePortal()"
      />
      <div v-show="showPortal">
        <!-- Portal Users -->
        <div class="setup-row">
          <div class="section-content">
            <VasionListSelection
              :available-fields.sync="availablePortalUsers"
              :selected-fields.sync="assignedPortalUsers"
              :allow-field-ordering="false"
              available-label-text="Users"
              selected-label-text="Permitted Users"
              unique-id="permissions-portal-users"
              @update:availableFields="emitPermissionsChanged"
              @update:selectedFields="emitPermissionsChanged"
              @update:addAll="emitPermissionsChanged"
              @update:removeAll="emitPermissionsChanged"
            />
          </div>
        </div>
        <div class="subsection-separator"></div>
        <!-- Portal Groups -->
        <div class="setup-row">
          <div class="section-content">
            <VasionListSelection
              :available-fields.sync="availablePortalGroups"
              :selected-fields.sync="assignedPortalGroups"
              :allow-field-ordering="false"
              available-label-text="Groups"
              selected-label-text="Permitted Groups"
              unique-id="permissions-portal-groups"
              @update:availableFields="emitPermissionsChanged"
              @update:selectedFields="emitPermissionsChanged"
              @update:addAll="emitPermissionsChanged"
              @update:removeAll="emitPermissionsChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserGroupPermissions',
  props: {
    users: {
      type: Array,
      default: () => [],
      required: true,
    },
    groups: {
      type: Array,
      default: () => [],
      required: true,
    },
    initialPermissions: {
      type: Object,
      default: () => ({
        userAccess: [],
        groupAccess: [],
      }),
    },
  },
  data: function () {
    return {
      assignedInternalGroups: [],
      assignedInternalUsers: [],
      assignedPortalGroups: [],
      assignedPortalUsers: [],
      availableInternalGroups: [],
      availableInternalUsers: [],
      availablePortalGroups: [],
      availablePortalUsers: [],
      showInternal: true,
      showPortal: true,
    }
  },
  mounted() {
    this.initializeData()
  },
  watch: {
    users() {
      this.$nextTick(() => {
        this.initializeData()
      })
    },
    groups() {
      this.$nextTick(() => {
        this.initializeData()
      })
    },
    initialPermissions() {
      this.$nextTick(() => {
        this.initializeData()
      })
    },
  },
  methods: {
    toggleInternal() {
      this.showInternal = !this.showInternal
    },
    togglePortal() {
      this.showPortal = !this.showPortal
    },
    getCurrentPermissions() {
      // Combine internal and portal users and groups
      const permissions = {
        assignedUsers: [
          ...this.assignedInternalUsers.map(user => user?.value).filter(e => e != null),
          ...this.assignedPortalUsers.map(user => user?.value).filter(e => e != null),
        ],
        assignedGroups: [
          ...this.assignedInternalGroups.map(group => group?.value).filter(e => e != null),
          ...this.assignedPortalGroups.map(group => group?.value).filter(e => e != null),
        ],
      }

      return permissions
    },
    initializeData() {
      // Reset all arrays
      this.assignedInternalUsers = []
      this.assignedPortalUsers = []
      this.availableInternalUsers = []
      this.availablePortalUsers = []
      this.assignedInternalGroups = []
      this.assignedPortalGroups = []
      this.availableInternalGroups = []
      this.availablePortalGroups = []
      // Process users
      this.users.forEach(element => {
        const user = {
          value: element.UserId || element.ID || element.value,
          name: element.Username || element.Name || element.name,
          isPortalUser: element.IsPortalUser || element.isPortalUser || false,
        }

        if (user.isPortalUser) {
          this.availablePortalUsers.push(user)
        } else {
          this.availableInternalUsers.push(user)
        }
      })
      // Process groups
      this.groups.forEach(element => {
        const group = {
          value: element.ID || element.value,
          name: element.Name || element.name,
          isPortalGroup: element.isPortalGroup || false,
        }

        if (group.isPortalGroup) {
          this.availablePortalGroups.push(group)
        } else {
          this.availableInternalGroups.push(group)
        }
      })
      // Apply initial permissions
      this.applyInitialPermissions()
    },
    applyInitialPermissions() {
      if (!this.initialPermissions) return
      // Apply user permissions
      if (this.initialPermissions.userAccess && this.initialPermissions.userAccess.length > 0) {
        this.initialPermissions.userAccess.forEach(userId => {
          // Find user in available internal users
          let userIndex = this.availableInternalUsers.findIndex(user => user.value === userId)
          if (userIndex !== -1) {
            const user = this.availableInternalUsers.splice(userIndex, 1)[0]
            this.assignedInternalUsers.push(user)
          } else {
            // If not found in internal users, check portal users
            userIndex = this.availablePortalUsers.findIndex(user => user.value === userId)
            if (userIndex !== -1) {
              const user = this.availablePortalUsers.splice(userIndex, 1)[0]
              this.assignedPortalUsers.push(user)
            }
          }
        })
      }
      // Apply group permissions
      if (this.initialPermissions.groupAccess && this.initialPermissions.groupAccess.length > 0) {
        this.initialPermissions.groupAccess.forEach(groupId => {
          // Find group in available internal groups
          let groupIndex = this.availableInternalGroups.findIndex(group => group.value === groupId)
          if (groupIndex !== -1) {
            const group = this.availableInternalGroups.splice(groupIndex, 1)[0]
            this.assignedInternalGroups.push(group)
          } else {
            // If not found in internal groups, check portal groups
            groupIndex = this.availablePortalGroups.findIndex(group => group.value === groupId)
            if (groupIndex !== -1) {
              const group = this.availablePortalGroups.splice(groupIndex, 1)[0]
              this.assignedPortalGroups.push(group)
            }
          }
        })
      }
    },
    emitPermissionsChanged() {
      const currentPermissions = this.getCurrentPermissions()
      this.$emit('permissionsChanged', currentPermissions)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.separator-div {
  height: 40px;
}
.subsection-separator {
  height: 20px;
  clear: both;
}
.collapsible-section {
  float: left;
  margin-top: 20px;
  width: 100%;

  .subheader {
    @include SubHeadline;
    width: 200px;
    display: block;
    float: left;
    margin-top: 5px;
  }

  .collapse-section {
    float: left;
  }
}
.setup-row {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 6px 0;
  clear: both;
}
.section-content {
  width: 100%;
}
.subsection-header {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: $grey-700;
}
</style>
